import { useMemo, useState } from 'react'
import { parseMetadata } from '../utils'
import { useAppSelector } from '../../../../../store/hooks'
import { User } from '../../../../../models/User'
import { ASSIGNMENT_ROLE_NAME_MAPPING } from '../ConfigureAssignment/AssignmentRoleNameMapping'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { UserConsent } from '../../../../../components/Consent/model'
import { RoomSession } from '../../../../../models/Room'
import { useConsentCheck } from '../hooks'

export const ROLES_PER_PAGE = 3

export const SPEAKER_DISPLAY_THRESHOLD = 0.005
export const DEFAULT_ROLE = 'Clients'

export type UsersConsent = {
  [key: string]: UserConsent | null
}

export const useMeetingUsers = (roomSession: RoomSession | null) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId })

  const [currentPage, setCurrentPage] = useState<number>(1)

  const { hasQuota } = useZocksQuota()

  const consentTrackingAvailable = hasQuota('ai.userConsentTracking.value')
  const consentTrackingEnabled = !!account?.data.effectiveConfig.complianceConfig.userConsentTrackingEnabled
  const roomId = roomSession?.roomId
  const sessionId = roomSession?.id

  const { usersConsent } = useConsentCheck({ roomId, sessionId, skip: !roomSession || !consentTrackingAvailable || !consentTrackingEnabled })

  const speakingDistribution = useMemo(() => {
    if (!roomSession) {
      return {}
    }
    const metadata = parseMetadata(roomSession.metadata)
    const { participantSpeakingDistribution } = metadata
    return participantSpeakingDistribution ?? {}
  }, [roomSession])

  const usersByRoles = useMemo(() => {
    const { users } = roomSession ?? {}
    if (!users) {
      return {}
    }
    return users.reduce((acc: { [key: string]: User[] }, user) => {
      const speakingDurationPercent = speakingDistribution[user.id] ?? 0
      if (speakingDurationPercent > SPEAKER_DISPLAY_THRESHOLD || !user.id.startsWith('speaker_')) {
        const roleName = !user.roles.length ? DEFAULT_ROLE : (ASSIGNMENT_ROLE_NAME_MAPPING[user.roles[0].id] ?? user.roles[0].name)
        if (!acc[roleName]) {
          acc[roleName] = []
        }
        acc[roleName].push(user)
      }
      return acc
    }, {})
  }, [roomSession, speakingDistribution])

  const pages = Math.ceil(Object.keys(usersByRoles).length / ROLES_PER_PAGE)

  const rolesOnCurrentPage = Object.keys(usersByRoles).slice((currentPage - 1) * ROLES_PER_PAGE, currentPage * ROLES_PER_PAGE)
  const usersByRolesOnCurrentPage = Object.keys(usersByRoles)
    .filter((role) => rolesOnCurrentPage.includes(role))
    .reduce((obj: { [key: string]: User[] }, role) => {
      obj[role] = usersByRoles[role]
      return obj
    }, {})

  const getSpeakingPercentage = (user: User) => {
    const { id: userId } = user
    const distribution = Math.round(100 * speakingDistribution[userId])
    return isNaN(distribution) ? 0 : distribution
  }

  const nextPage = () => {
    if (currentPage < pages) {
      setCurrentPage((currentPage) => currentPage + 1)
    }
  }

  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((currentPage) => currentPage - 1)
    }
  }

  return { usersByRoles, usersByRolesOnCurrentPage, pages, currentPage, nextPage, previousPage, usersConsent, speakingDistribution, getSpeakingPercentage }
}
